import { useEffect, useState } from 'react';

import { FolderInput } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { InconsistencyFiltersPopup } from './InconsistencyFilters';
import DataViewToolbar from '../../components/TableToolbar';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateInconsistencyReport } from '@/api/mutations/useCreateInconsistencyReport';
import { Button } from '@/components/atoms/new-button';
import { FilterExpandableButton } from '@/components/molecules/FilterExpandableButton';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import SearchBox from '@/components/molecules/SearchBox';
import Pagination from '@/components/organisms/Pagination';
import { InconsistencyFilters } from '@/types';

interface InconsistenciesEditorToolbarProps {
  caseId: string;
  selectedInconsistencyIds: string[];
  totalCount: number;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  onSearchCall: () => void;
  clearSearch: () => void;
  currentPage: number;
  totalPages?: number;
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
  activeFilters?: InconsistencyFilters;
  setFilters?: (v: InconsistencyFilters) => void;
}

const InconsistenciesEditorToolbar = ({
  caseId,
  selectedInconsistencyIds,
  globalFilter,
  setGlobalFilter,
  onSearchCall,
  clearSearch,
  currentPage,
  totalPages,
  prevPage,
  nextPage,
  goToPage,
  activeFilters,
  setFilters,
}: InconsistenciesEditorToolbarProps) => {
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);

  const { mutateAsync: createInconsistencyReport } = useCreateInconsistencyReport(caseId);

  const navigate = useNavigate();

  useEffect(() => {
    if (activeFilters) {
      const count = Object.values(activeFilters).filter(
        (value) =>
          value !== undefined &&
          value !== null &&
          value !== false &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0),
      ).length;
      setActiveFilterCount(count);
    }
  }, [activeFilters]);

  return (
    <>
      <DataViewToolbar
        titleSection={<h1 className="text-lg font-semibold">Inconsistencies</h1>}
        pagination={
          <Pagination
            canGetPrevPage={currentPage > 0}
            canGetNextPage={totalPages !== undefined && currentPage < totalPages - 1}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={currentPage}
            noOfPages={totalPages}
            goToPage={goToPage}
          />
        }
        inlineSearchBar={
          <SearchBox
            value={globalFilter}
            placeholder={`Search...`}
            onChange={setGlobalFilter}
            onSearchCall={onSearchCall}
            clearSearch={clearSearch}
          />
        }
        actionButtons={
          <Button
            disabled={selectedInconsistencyIds.length === 0}
            className="h-8 rounded-sm text-xs"
            variant="secondary"
            onClick={async () => {
              const report = await createInconsistencyReport(selectedInconsistencyIds);
              trackCustomEvent(TrackingEvents.CreateInconsistencyReport, { caseId });
              navigate(
                `/app/chronos/case-editor/data/inconsistency-report/view?caseId=${caseId}&reportId=${report.id}&page=1&title=${report.title}`,
              );
            }}
          >
            <FolderInput className="h-3 w-3" />
            Create Report
          </Button>
        }
        filterButtons={
          <FilterExpandableButton filterCount={activeFilterCount} onClick={() => setFiltersModalOpen(true)} />
        }
      />

      {activeFilters && setFilters && (
        <CustomModal
          maxWidth="42rem"
          content={
            <InconsistencyFiltersPopup
              activeFilters={activeFilters}
              setFilters={setFilters}
              handleClose={() => setFiltersModalOpen(false)}
              goToPage={goToPage}
            />
          }
          isOpen={filtersModalOpen}
          handleClose={() => setFiltersModalOpen(false)}
        />
      )}
    </>
  );
};

export default InconsistenciesEditorToolbar;
